import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { publicErrorMessage } from '../../public/auth-errors.js';
import { PUBLIC_FORM } from '../../public/classes.js';
import { PasswordInput } from '../../public/PasswordInput.js';
import { PublicShell } from '../../public/PublicShell.js';
import { api, ApiError } from '../../shared/api.js';
import { Button, buttonClasses } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { FormActions } from '../../ui/FormActions.js';
import { Alert } from '../../ui/States.js';
import { clearHash, readHashToken } from '../lib/hash.js';

const MIN_LENGTH = 10;

/** Lien inutilisable (incomplet, expiré ou déjà servi) : explication et nouveau lien. */
function InvalidLink({ incomplete }: { incomplete: boolean }) {
  return (
    <PublicShell title="Lien expiré" back={{ to: '/login', label: 'Connexion' }}>
      <Alert tone="warning" title={incomplete ? 'Ce lien est incomplet.' : 'Ce lien n’est plus valable.'}>
        {incomplete
          ? 'Ouvrez à nouveau le lien reçu par e-mail, en entier, ou demandez-en un nouveau.'
          : 'Un lien de réinitialisation est valable une heure et ne sert qu’une fois. Demandez-en un nouveau.'}
      </Alert>
      <Link to="/forgot-password" className={buttonClasses({ size: 'lg', block: true, className: 'mt-6' })}>
        Demander un nouveau lien
      </Link>
    </PublicShell>
  );
}

/** Nouveau mot de passe, depuis le lien reçu par e-mail (jeton dans le fragment `#`). */
export default function ResetPasswordPage() {
  const [token] = useState(readHashToken);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const reset = useMutation({
    mutationFn: () => api('auth.passwordResetConfirm', { body: { token, password } }),
    onSuccess: () => clearHash(),
  });

  if (!token) return <InvalidLink incomplete />;
  if (reset.error instanceof ApiError && reset.error.status === 400) return <InvalidLink incomplete={false} />;

  if (reset.isSuccess) {
    return (
      <PublicShell title="Mot de passe modifié">
        <Alert tone="success">Votre nouveau mot de passe est enregistré. Vous pouvez vous connecter.</Alert>
        <Link to="/login" className={buttonClasses({ size: 'lg', block: true, className: 'mt-6' })}>
          Se connecter
        </Link>
      </PublicShell>
    );
  }

  const apiError = publicErrorMessage(reset.error);
  return (
    <PublicShell title="Nouveau mot de passe" intro={`Choisissez un mot de passe d’au moins ${MIN_LENGTH} caractères.`}>
      <form
        className={PUBLIC_FORM}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const next: typeof errors = {};
          if (password.length < MIN_LENGTH) next.password = `Au moins ${MIN_LENGTH} caractères (${password.length} saisis).`;
          else if (password !== confirm) next.confirm = 'Les deux mots de passe ne correspondent pas.';
          setErrors(next);
          if (next.password || next.confirm) return;
          reset.mutate();
        }}
      >
        <Field label="Nouveau mot de passe" error={errors.password}>
          <PasswordInput
            name="new-password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Confirmer le mot de passe" error={errors.confirm}>
          <PasswordInput
            name="confirm-password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {apiError ? (
          <Alert tone="danger" role="alert">
            {apiError}
          </Alert>
        ) : null}
        <FormActions>
          <Button type="submit" size="lg" block loading={reset.isPending}>
            Enregistrer le mot de passe
          </Button>
        </FormActions>
      </form>
    </PublicShell>
  );
}
